import { Button, Tooltip, Link } from '@paypalcorp/pp-react'
import { default as FileSaver } from 'file-saver'
import React, { FunctionComponent, useContext } from 'react'
import { Accordion } from '../../../components/integration-builder/accordion'
import { CheckoutSimulatorContext } from '../../../components/integration-builder/context/CheckoutSimulatorContext'
import withAnalytics from '../../../utils/withAnalytics'
import styles from '../integration-builder.module.css'
import { ExternalLink } from '../../../components/icons'

const Build: FunctionComponent = () => {
  const { integrationType, options, parameters } = useContext(
    CheckoutSimulatorContext,
  )
  const fptiData = typeof window !== `undefined` && window.fpti

  const frontendLanguageMap: { [x: string]: string } = {
    html: 'HTML',
    react: 'React',
    angular: 'Angular',
  }

  const backendLanguageMap: { [x: string]: string } = {
    java: 'Java',
    nodejs: 'NodeJS',
  }

  let selectedBackendLanguage =
    options?.find((option) => option.name === 'backend')?.value || 'nodejs'

  let selectedFrontendLanguage =
    options?.find((option) => option.name === 'frontend')?.value || 'html'

  const handleDownload = () => {
    // THIS IS NEEDED SINCE THE REPOSITORY CONTAINS NODE AND THE LOGIC IN CONTEXT IS PROPERLY CALLED NODEJS
    const languageBack =
      selectedBackendLanguage === 'nodejs' ? 'node' : selectedBackendLanguage
    let file = ''
    if (integrationType === 'advanced') {
      if (parameters?.buttons.includes('payment-buttons_3ds')) {
        file =
          'https://paypalobjects.com/ppdevdocs/static/zipped_samples/advanced/3ds-node.zip'
      } else {
        file = `https://paypalobjects.com/ppdevdocs/static/zipped_samples/advanced/${options[1].value}-${languageBack}.zip`
      }
    } else {
      file = `https://paypalobjects.com/ppdevdocs/static/zipped_samples/standard/${options[1].value}-${languageBack}.zip`
    }
    FileSaver.saveAs(file, `${file.split('/').at(-1)}`)

    // CAPTURE ANALYTICS
    if (fptiData && window.PAYPAL?.analytics?.instance) {
      const event = `download_${options[1].value}_${languageBack}_${
        integrationType === 'advanced' ? 'ac' : 'sc'
      }`
      window.PAYPAL.analytics.instance.record({
        data: {
          component: 'devdiscoverynodeweb',
          page: 'main:developer:integration-builder',
          pgrp: 'main:developer:integration-builder',
          e: 'cl',
          link: event,
          event_action: event,
          ...fptiData,
        },
      })
    }
  }

  return (
    <div className={styles.checkout_simulator__accordion_container}>
      <Accordion
        number={3}
        color="secondary"
        heading="Build in the PayPal sandbox"
        defaultCollapsed
        content={
          <>
            <div className={styles.checkout_simulator__subtitle_wrapper}>
              <p className={styles.checkout_simulator__fragment_subtitle}>
                Build your application and test in the sandbox
              </p>
              <Tooltip
                name="tooltip1"
                description="Test your application with transactions in the PayPal sandbox."
                windowId="window1"
                imagePosition="bottom-left"
              />
            </div>
            <ol>
              <li>
                Copy the sample <span>JavaScript SDK</span> code to your
                checkout page.
              </li>
              <li>Optional: Customize your code.</li>
              <li>
                {integrationType === 'advanced' &&
                selectedBackendLanguage === 'nodejs' &&
                selectedFrontendLanguage === 'html' ? (
                  <>
                    Replace <span>{`<test>`}</span> client ID in the SDK{' '}
                    <span>{`<script src>`}</span> with your own sandbox Business
                    Account app client ID in the{' '}
                    {frontendLanguageMap[selectedFrontendLanguage!.toString()]}{' '}
                    file and then replace the CLIENT ID and APP_SECRET in the
                    .env file in the back end file when downloading the whole
                    sample application from the{' '}
                    <Link
                      target="_blank"
                      href={`${
                        parameters?.buttons.includes('payment-buttons_3ds')
                          ? 'https://github.com/paypaldev/PayPal-JavaScript-FullStack-3Ds-Advanced-Checkout-Sample'
                          : 'https://github.com/paypaldev/PayPal-JavaScript-FullStack-Advanced-Checkout-Sample'
                      }`}
                      className={styles.integration_builder__external_link}
                    >
                      GitHub
                    </Link>
                    <ExternalLink />.
                  </>
                ) : (
                  <>
                    Replace <span>{`<test>`}</span> client ID in the SDK{' '}
                    <span>{`<script src>`}</span> with your own sandbox Business
                    Account app client ID. These credentials are available in
                    the{' '}
                    <span>
                      <Link
                        target="_blank"
                        href="/dashboard/"
                        className={styles.integration_builder__external_link}
                      >
                        Developer Dashboard
                      </Link>
                      <ExternalLink />.
                    </span>
                  </>
                )}
              </li>
              {integrationType === 'advanced' ? (
                <li>
                  In Sandbox App Settings, go to Accept Payments {'>'} Advanced
                  Options. Make sure the Advanced Credit and Debit Card Payments
                  checkbox is checked.
                </li>
              ) : null}
              <li>
                Test your sandbox integration by following the{' '}
                <span>
                  <Link
                    target="_blank"
                    href={`/docs/checkout/${integrationType}/integrate/`}
                    className={styles.integration_builder__external_link}
                  >
                    integration guide
                  </Link>
                  <ExternalLink />.
                </span>
              </li>
              {integrationType === 'standard' ? (
                <li>
                  Checkout the sample application with{' '}
                  {frontendLanguageMap[selectedFrontendLanguage!.toString()]}{' '}
                  and {backendLanguageMap[selectedBackendLanguage!.toString()]}{' '}
                  combination in{' '}
                  <span>
                    <Link
                      target="_blank"
                      href={
                        selectedFrontendLanguage === 'html'
                          ? 'https://github.com/paypaldev/PayPal-JavaScript-FullStack-Standard-Checkout-Sample/tree/main'
                          : 'https://github.com/paypaldev/PayPal-React-FullStack-Standard-Checkout-Sample'
                      }
                      className={styles.integration_builder__external_link}
                    >
                      GitHub
                    </Link>
                    <ExternalLink />.
                  </span>
                </li>
              ) : (
                <li>
                  Checkout the sample application with{' '}
                  {frontendLanguageMap[selectedFrontendLanguage!.toString()]}{' '}
                  and {backendLanguageMap[selectedBackendLanguage!.toString()]}{' '}
                  combination in{' '}
                  <span>
                    <Link
                      target="_blank"
                      href={
                        parameters?.buttons.includes('payment-buttons_3ds')
                          ? 'https://github.com/paypaldev/PayPal-JavaScript-FullStack-3Ds-Advanced-Checkout-Sample'
                          : 'https://github.com/paypaldev/PayPal-JavaScript-FullStack-Advanced-Checkout-Sample'
                      }
                      className={styles.integration_builder__external_link}
                    >
                      GitHub
                    </Link>
                    <ExternalLink />.
                  </span>
                </li>
              )}
            </ol>
            <Button
              id="download"
              className={styles.checkout_simulator__download_button}
              title="download"
              secondary
              onClick={() => handleDownload()}
            >
              Download
            </Button>
          </>
        }
      />
    </div>
  )
}

export default withAnalytics(Build)
